/**
 * Public API Client
 * 
 * Handles unauthenticated API endpoints that don't require Google ID token.
 * These include values definitions, regions, states, and other public data.
 * 
 * @author OriginSelect Team
 * @created December 18, 2025
 */

import axios from 'axios';

// Environment detection
const isDevelopment = process.env.NODE_ENV === 'development';
const awsServerUrl = 'https://d3d8378t728e1f.cloudfront.net';
const apiBaseURL = process.env.REACT_APP_API_BASE_URL || awsServerUrl;

console.log('🌐 [PublicAPI] Configuration:', {
  isDevelopment,
  baseURL: apiBaseURL,
  note: 'Using CloudFront directly (CORS-enabled, bypasses dev proxy issues)'
});

// Create axios instance for Public API (no authentication required)
// Always use CloudFront directly — these are public endpoints with CORS headers,
// so no need to rely on the dev proxy which can return stale S3 HTML.
const PublicAPI = axios.create({
  baseURL: apiBaseURL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: false
});

// Validate responses are JSON (not HTML from S3 fallback)
PublicAPI.interceptors.response.use(
  response => {
    // Detect HTML responses that should be JSON (CloudFront routing issue)
    if (typeof response.data === 'string' && response.data.trim().startsWith('<!doctype')) {
      console.error('🌐 [PublicAPI] Received HTML instead of JSON for:', response.config.url);
      return Promise.reject(new Error(`Received HTML instead of JSON for ${response.config.url}`));
    }
    return response;
  }
);

// Debug logging in development
if (isDevelopment) {
  PublicAPI.interceptors.request.use(config => {
    console.log('🌐 [PublicAPI] Request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      params: config.params,
      fullURL: (config.baseURL || '') + (config.url || '')
    });
    return config;
  });

  PublicAPI.interceptors.response.use(
    response => {
      console.log('🌐 [PublicAPI] Response:', {
        status: response.status,
        url: response.config.url,
        dataType: typeof response.data,
        dataLength: response.data ? JSON.stringify(response.data).length : 0
      });
      return response;
    },
    error => {
      console.error('🌐 [PublicAPI] Error:', {
        status: error.response?.status,
        message: error.message,
        url: error.config?.url,
        response: error.response?.data ? (typeof error.response.data === 'string' ? error.response.data.substring(0, 100) : error.response.data) : 'none'
      });
      return Promise.reject(error);
    }
  );
}

/**
 * Fetch USA primary values definitions
 * @returns {Promise<Object>} Values definitions for USA
 */
export const fetchUSAPrimaryValues = async () => {
  try {
    const response = await PublicAPI.get('/api/values-definitions/primary/usa');
    return response.data;
  } catch (error) {
    console.error('fetchUSAPrimaryValues failed:', error.message);
    throw error;
  }
};

/**
 * Fetch Canada primary values definitions
 * @returns {Promise<Object>} Values definitions for Canada
 */
export const fetchCanadaPrimaryValues = async () => {
  try {
    const response = await PublicAPI.get('/api/values-definitions/primary/canada');
    return response.data;
  } catch (error) {
    console.error('fetchCanadaPrimaryValues failed:', error.message);
    throw error;
  }
};

/**
 * Fetch USA states
 * @returns {Promise<Object>} USA states data
 */
export const fetchUSAStates = async () => {
  try {
    const response = await PublicAPI.get('/api/regions/usa');
    return response.data;
  } catch (error) {
    console.error('fetchUSAStates failed:', error.message);
    throw error;
  }
};

/**
 * Fetch Canadian provinces
 * @returns {Promise<Object>} Canadian provinces data
 */
export const fetchCanadianProvinces = async () => {
  try {
    const response = await PublicAPI.get('/api/regions/canada');
    return response.data;
  } catch (error) {
    console.error('fetchCanadianProvinces failed:', error.message);
    throw error;
  }
};

export default PublicAPI;
